import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StatusProvider } from "@/context/StatusContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import Board from "@/pages/Board";
import JobDetail from "@/pages/JobDetail";
import Users from "@/pages/Users";
import Locations from "@/pages/Locations";
import Departments from "@/pages/Departments";
import JobStatuses from "@/pages/JobStatuses";
import ActivityLog from "@/pages/ActivityLog";
import Reports from "@/pages/Reports";
import Tenants from "@/pages/Tenants";
import Security from "@/pages/Security";
import AdminUsers from "@/pages/AdminUsers";
import Equipment from "@/pages/Equipment";
import HireDashboard from "@/pages/HireDashboard";
import Branding from "@/pages/Branding";
import Layout from "@/components/Layout";
import HireDocket from "@/pages/HireDocket";
import ServiceJobs from "@/pages/ServiceJobs";
import Schedule from "@/pages/Schedule";

// Route role sets hoisted to module scope so the arrays keep a stable identity
// across renders (avoids re-rendering Protected/route elements unnecessarily).
const ROLES_JOBS = ["admin", "manager", "rigger", "job_dispatcher", "viewer"];
const ROLES_USERS = ["admin", "manager"];
const ROLES_ADMIN = ["admin"];
const ROLES_REPORTS = ["admin", "manager"];
const ROLES_HIRE = ["admin", "manager", "hire_manager", "hire_coordinator"];
const ROLES_SUPER = ["superadmin"];
const ROLES_SERVICE = ["admin", "manager", "hire_manager", "hire_coordinator", "service_technician"];

const BareShell = ({ children }) => children;

function Protected({ children: page, roles, bare = false }) {
  const { user, loading, activeTenant } = useAuth();
  const children = page;
  const Shell = bare ? BareShell : Layout;
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 text-zinc-500 font-mono text-sm">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  // Super admin lives in the Tenants console; may manage a chosen tenant with full admin access
  if (user.role === "superadmin") {
    if (roles?.includes("superadmin")) return <Shell>{children}</Shell>;
    // Security page (change password / 2FA) must be reachable without a tenant context
    if (window.location.pathname === "/security") return <Shell>{children}</Shell>;
    if (!activeTenant) return <Navigate to="/tenants" replace />;
    return <Shell>{children}</Shell>;
  }
  // Hire manager only reaches Equipment Hire and the Hire Dashboard
  if (user.role === "hire_manager" || user.role === "hire_coordinator") {
    if (roles?.includes(user.role) || window.location.pathname === "/security") return <Shell>{children}</Shell>;
    return <Navigate to="/hire-dashboard" replace />;
  }
  if (user.role === "service_technician") {
    if (roles?.includes(user.role) || window.location.pathname === "/security") return <Shell>{children}</Shell>;
    return <Navigate to="/service-jobs" replace />;
  }
  if (user.role === "rigger" && user.dual_role && roles?.includes("service_technician")) return <Shell>{children}</Shell>;
  // Job dispatcher reaches the Job Dashboard and the Jobs area
  if (user.role === "job_dispatcher") {
    const path = window.location.pathname;
    if (path === "/" || path.startsWith("/jobs") || path === "/board" || path === "/security") return <Shell>{children}</Shell>;
    return <Navigate to="/" replace />;
  }
  // Viewer has read-only access to the Job Dashboard and Jobs only
  if (user.role === "viewer") {
    const path = window.location.pathname;
    if (path === "/" || path.startsWith("/jobs") || path === "/board" || path === "/security") return <Shell>{children}</Shell>;
    return <Navigate to="/" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Shell>{children}</Shell>;
}

function App() {
  useEffect(() => {
    const hide = () => {
      const badge = document.getElementById("emergent-badge");
      if (badge) badge.style.setProperty("display", "none", "important");
    };
    hide();
    const observer = new MutationObserver(hide);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <StatusProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Protected><Dashboard /></Protected>} />
              <Route path="/jobs" element={<Protected roles={ROLES_JOBS}><Jobs /></Protected>} />
              <Route path="/board" element={<Protected roles={ROLES_JOBS}><Board /></Protected>} />
              <Route path="/jobs/:id" element={<Protected roles={ROLES_JOBS}><JobDetail /></Protected>} />
              <Route path="/users" element={<Protected roles={ROLES_USERS}><Users /></Protected>} />
              <Route path="/locations" element={<Protected roles={ROLES_ADMIN}><Locations /></Protected>} />
              <Route path="/departments" element={<Protected roles={ROLES_ADMIN}><Departments /></Protected>} />
              <Route path="/job-statuses" element={<Protected roles={ROLES_ADMIN}><JobStatuses /></Protected>} />
              <Route path="/branding" element={<Protected roles={ROLES_ADMIN}><Branding /></Protected>} />
              <Route path="/activity" element={<Protected roles={ROLES_ADMIN}><ActivityLog /></Protected>} />
              <Route path="/reports" element={<Protected roles={ROLES_REPORTS}><Reports /></Protected>} />
              <Route path="/equipment" element={<Protected roles={ROLES_HIRE}><Equipment /></Protected>} />
              <Route path="/hire-orders/:id/docket" element={<Protected roles={ROLES_HIRE} bare><HireDocket /></Protected>} />
              <Route path="/service-jobs" element={<Protected roles={ROLES_SERVICE}><ServiceJobs /></Protected>} />
              <Route path="/schedule" element={<Protected roles={ROLES_SERVICE}><Schedule /></Protected>} />
              <Route path="/hire-dashboard" element={<Protected roles={ROLES_HIRE}><HireDashboard /></Protected>} />
              <Route path="/tenants" element={<Protected roles={ROLES_SUPER}><Tenants /></Protected>} />
              <Route path="/admin/users" element={<Protected roles={ROLES_SUPER}><AdminUsers /></Protected>} />
              <Route path="/security" element={<Protected><Security /></Protected>} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </StatusProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
